import { SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';

import { OrderType } from '@cds/betting-offer';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { CrmPromoIdsMap, EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { ParticipantImageComponent, ParticipantImageService, ParticipantType } from '@frontend/sports/participant-image/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { ShowInEpcotDirective } from '../common/epcot-config.directive';
import { BadgeComponent } from '../crm-promotion/badge.component';
import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { MarketAttributeOptionNameStrategy } from './market-attribute-option-name.strategy';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroup, OptionGroupService } from './option-group.service';
import { OptionNameStrategy } from './option-name.strategy';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState, ShowMoreData } from './option-panel-bottom-action.component';
import { OptionComponent } from './option.component';
import { PlayerStatsComponent } from './player-stats/player-stats.component';

@Component({
    selector: 'ms-player-combined-option-group',
    templateUrl: './player-combined-option-group.component.html',
    styleUrl: './player-combined-option-group.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: OptionNameStrategy, useClass: MarketAttributeOptionNameStrategy }, ParticipantImageService],
    imports: [
        ShowInEpcotDirective,
        MarketHelpComponent,
        BadgeComponent,
        OptionComponent,
        PlayerStatsComponent,
        IconCustomComponent,
        SlicePipe,
        OptionPanelBottomActionComponent,
        ParticipantImageComponent,
    ],
})
export class PlayerCombinedOptionGroupComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    @Input() showPlayerStats = false;
    @Output() showAllRequested = new EventEmitter<void>();

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    showMoreData: ShowMoreData;

    optionGroups: OptionGroup[];

    expanded = false;
    visibleOptionsLimit: number | undefined;
    isShowHeadshotImage: boolean;
    participantType = ParticipantType;

    constructor(
        public sitecore: Sitecore,
        private optionGroupService: OptionGroupService,
        private marketGroupingConfig: MarketGroupingConfig,
        private participantImageService: ParticipantImageService,
    ) {}

    ngOnChanges(): void {
        this.optionGroups = this.optionGroupService.getOptionGroups(
            this.optionGroup,
            (option) => option.optionGroupId,
            (optionGroup) => (this.optionGroup.detailedGrouping.orderType === OrderType.LineValue ? optionGroup.optionAttribute : ''),
        );

        const totalOptions = this.optionGroups.length;
        this.calculateMaxNumberOfVisibleOptions();
        this.showMoreData = {
            totalCount: totalOptions,
            initialVisibleCount: this.optionsLimitOverride ?? this.marketGroupingConfig.playerCombinedGroupMaxLines,
            pageSize: totalOptions,
        };
        this.isShowHeadshotImage = this.participantImageService.showHeadshotImage(this.event.league.id, OptionGroupType.PlayerCombined);
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.expanded = state !== 'collapsed';
        this.calculateMaxNumberOfVisibleOptions();
    }

    isCrmMarketPromoted(optionGroup: OptionGroup): boolean {
        return (
            !!this.crmPromoIdsMap.markets[optionGroup.optionGroupId] ||
            (!!optionGroup.templateId && !!this.crmPromoIdsMap.templateIds[optionGroup.templateId])
        );
    }

    getPlayers(optionGroup: OptionGroup): number[] {
        const players: number[] = [];

        if (optionGroup.player1Id) {
            players.push(optionGroup.player1Id);
        }

        if (optionGroup.player2Id) {
            players.push(optionGroup.player2Id);
        }

        return players;
    }

    private calculateMaxNumberOfVisibleOptions(): void {
        this.visibleOptionsLimit =
            this.expanded || this.isInSheetView ? undefined : (this.optionsLimitOverride ?? this.marketGroupingConfig.playerCombinedGroupMaxLines);
    }
}
